import { useState, useEffect, useRef, useCallback } from "react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import {
  Building2, Search, MapPin, ExternalLink, RefreshCw,
  Calculator, DollarSign, Percent, BarChart3, ChevronDown, ChevronUp,
} from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : "/api";

const QUICK_MARKETS = [
  "Los Angeles, CA", "New York, NY", "Chicago, IL", "Miami, FL",
  "Dallas, TX", "Houston, TX", "Atlanta, GA", "Tampa, FL",
  "Phoenix, AZ", "Denver, CO", "Seattle, WA", "Boston, MA",
  "San Francisco, CA", "Nashville, TN", "Charlotte, NC", "Austin, TX",
];

function formatCurrency(n) {
  if (!n) return "$0";
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${Math.round(n).toLocaleString()}`;
}

function MetricBox({ label, value, color = "text-foreground" }) {
  return (
    <div className="bg-secondary/30 rounded-lg p-2 text-center">
      <p className={`text-sm sm:text-base font-mono font-bold ${color}`}>{value}</p>
      <p className="text-[8px] sm:text-[9px] text-muted-foreground uppercase mt-0.5">{label}</p>
    </div>
  );
}

// ─── Deal Calculator ──────────────────────────────────────────
function DealCalculator() {
  const [inputs, setInputs] = useState({
    price: "", cap_rate: "", noi: "", sqft: "", units: "",
    down_payment_pct: "25", interest_rate: "6.5", loan_term_years: "25",
    expense_ratio: "40", vacancy_rate: "5",
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const handleCalc = async () => {
    const price = parseFloat(inputs.price);
    if (!price || price <= 0) return;
    const capRate = parseFloat(inputs.cap_rate) || 0;
    const noi = parseFloat(inputs.noi) || 0;
    if (capRate <= 0 && noi <= 0) return;

    setLoading(true);
    try {
      const resp = await axios.post(`${API}/commercial/analyze`, {
        price, cap_rate: capRate, noi,
        sqft: parseFloat(inputs.sqft) || 0,
        units: parseInt(inputs.units) || 0,
        down_payment_pct: parseFloat(inputs.down_payment_pct) / 100,
        interest_rate: parseFloat(inputs.interest_rate) / 100,
        loan_term_years: parseInt(inputs.loan_term_years) || 25,
        expense_ratio: parseFloat(inputs.expense_ratio) / 100,
        vacancy_rate: parseFloat(inputs.vacancy_rate) / 100,
      });
      setResult(resp.data.analysis);
    } catch { setResult(null); }
    finally { setLoading(false); }
  };

  const set = (k, v) => setInputs(p => ({ ...p, [k]: v }));

  return (
    <Card className="bg-card border-border mb-6" data-testid="deal-calculator">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Calculator className="w-4 h-4 text-emerald-500" />
          <h3 className="font-semibold text-sm sm:text-base">Commercial Deal Calculator</h3>
        </div>
        <p className="text-[10px] sm:text-xs text-muted-foreground">Enter price + cap rate (or NOI) to get full investment analysis.</p>

        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          <div>
            <label className="text-[9px] text-muted-foreground uppercase mb-0.5 block">Price *</label>
            <div className="relative">
              <DollarSign className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
              <Input type="number" placeholder="2500000" value={inputs.price} onChange={e => set("price", e.target.value)} className="pl-6 h-8 text-xs" data-testid="calc-price" />
            </div>
          </div>
          <div>
            <label className="text-[9px] text-muted-foreground uppercase mb-0.5 block">Cap Rate %</label>
            <div className="relative">
              <Percent className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
              <Input type="number" step="0.1" placeholder="7.5" value={inputs.cap_rate} onChange={e => set("cap_rate", e.target.value)} className="pl-6 h-8 text-xs" data-testid="calc-cap-rate" />
            </div>
          </div>
          <div>
            <label className="text-[9px] text-muted-foreground uppercase mb-0.5 block">NOI</label>
            <div className="relative">
              <DollarSign className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
              <Input type="number" placeholder="187500" value={inputs.noi} onChange={e => set("noi", e.target.value)} className="pl-6 h-8 text-xs" data-testid="calc-noi" />
            </div>
          </div>
          <div>
            <label className="text-[9px] text-muted-foreground uppercase mb-0.5 block">Sqft</label>
            <Input type="number" placeholder="12000" value={inputs.sqft} onChange={e => set("sqft", e.target.value)} className="h-8 text-xs" data-testid="calc-sqft" />
          </div>
          <div>
            <label className="text-[9px] text-muted-foreground uppercase mb-0.5 block">Units</label>
            <Input type="number" placeholder="12" value={inputs.units} onChange={e => set("units", e.target.value)} className="h-8 text-xs" data-testid="calc-units" />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground transition-colors" onClick={() => setShowAdvanced(!showAdvanced)} data-testid="calc-advanced-toggle">
            {showAdvanced ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            Loan & Expense Assumptions
          </button>
          <Button size="sm" onClick={handleCalc} disabled={loading} className="ml-auto bg-emerald-600 hover:bg-emerald-700 text-white h-7 text-xs" data-testid="calc-analyze-btn">
            {loading ? <RefreshCw className="w-3 h-3 animate-spin" /> : <BarChart3 className="w-3 h-3 mr-1" />}
            Analyze Deal
          </Button>
        </div>

        {showAdvanced && (
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 p-2.5 bg-secondary/20 rounded-lg">
            {[
              ["down_payment_pct", "Down %"], ["interest_rate", "Interest %"], ["loan_term_years", "Loan Term"],
              ["expense_ratio", "Expense %"], ["vacancy_rate", "Vacancy %"],
            ].map(([k, label]) => (
              <div key={k}>
                <label className="text-[8px] text-muted-foreground uppercase mb-0.5 block">{label}</label>
                <Input type="number" step="0.1" value={inputs[k]} onChange={e => set(k, e.target.value)} className="h-7 text-[10px]" />
              </div>
            ))}
          </div>
        )}

        {result && (
          <div className="space-y-3 pt-3 border-t border-border" data-testid="calc-results">
            <div className="flex items-center gap-3">
              <div className={`text-2xl font-mono font-bold ${result.deal_score >= 60 ? "text-emerald-400" : result.deal_score >= 30 ? "text-amber-400" : "text-red-400"}`}>
                {result.deal_score}
              </div>
              <div>
                <p className="text-xs font-medium">Deal Score</p>
                <p className="text-[9px] text-muted-foreground">Based on cap rate, cash-on-cash, DSCR, price/unit</p>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
              <MetricBox label="Cap Rate" value={`${result.cap_rate}%`} color={result.cap_rate >= 7 ? "text-emerald-400" : "text-amber-400"} />
              <MetricBox label="Cash-on-Cash" value={`${result.cash_on_cash_return}%`} color={result.cash_on_cash_return >= 8 ? "text-emerald-400" : result.cash_on_cash_return >= 5 ? "text-amber-400" : "text-red-400"} />
              <MetricBox label="DSCR" value={result.dscr} color={result.dscr >= 1.25 ? "text-emerald-400" : result.dscr >= 1.0 ? "text-amber-400" : "text-red-400"} />
              <MetricBox label="Debt Yield" value={`${result.debt_yield}%`} color="text-blue-400" />
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
              <MetricBox label="NOI" value={formatCurrency(result.noi)} color="text-emerald-400" />
              <MetricBox label="Monthly Cash Flow" value={formatCurrency(result.cash_flow_monthly)} color={result.cash_flow_monthly > 0 ? "text-emerald-400" : "text-red-400"} />
              <MetricBox label="Annual Cash Flow" value={formatCurrency(result.cash_flow_annual)} color={result.cash_flow_annual > 0 ? "text-emerald-400" : "text-red-400"} />
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
              <MetricBox label="Down Payment" value={formatCurrency(result.down_payment)} />
              <MetricBox label="Loan" value={formatCurrency(result.loan_amount)} />
              <MetricBox label="Monthly Mortgage" value={formatCurrency(result.monthly_mortgage)} />
              <MetricBox label="Total Cash In" value={formatCurrency(result.total_cash_invested)} />
            </div>

            {(result.price_per_sqft > 0 || result.price_per_unit > 0) && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
                {result.price_per_sqft > 0 && <MetricBox label="Price / Sqft" value={formatCurrency(result.price_per_sqft)} />}
                {result.price_per_unit > 0 && <MetricBox label="Price / Unit" value={formatCurrency(result.price_per_unit)} />}
                {result.rent_per_unit_monthly > 0 && <MetricBox label="Est. Rent / Unit" value={`${formatCurrency(result.rent_per_unit_monthly)}/mo`} color="text-blue-400" />}
                {result.grm > 0 && <MetricBox label="GRM" value={result.grm} />}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Main Tab ────────────────────────────────────────────────
const CommercialSearchTab = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [listingType, setListingType] = useState("all");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchedLocation, setSearchedLocation] = useState("");
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef(null);

  const doSearch = useCallback((query, type) => {
    if (!query) return;
    setLoading(true);
    setSearchedLocation(query);
    axios.get(`${API}/commercial/search`, { params: { q: query, listing_type: type || listingType } })
      .then(r => setResults(r.data.status === "error" ? { ...r.data, listings: [], total: 0 } : r.data))
      .catch(() => setResults(null))
      .finally(() => setLoading(false));
  }, [listingType]);

  const handleSearch = (e) => { e.preventDefault(); doSearch(searchQuery, listingType); };
  const handleQuickMarket = (m) => { setSearchQuery(m); doSearch(m, listingType); };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (searchedLocation) doSearch(searchedLocation, listingType); }, [listingType]);

  // Map
  useEffect(() => {
    if (!results?.listings?.length || !mapContainerRef.current) return;
    const initMap = async () => {
      const L = (await import("leaflet")).default;
      await import("leaflet/dist/leaflet.css");
      if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }
      const firstListing = results.listings[0];
      if (!firstListing?.lat || !firstListing?.lng) return;
      mapRef.current = L.map(mapContainerRef.current, { zoomControl: true }).setView([firstListing.lat, firstListing.lng], 11);
      L.tileLayer("https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png", { attribution: "&copy; OSM &copy; CARTO", maxZoom: 18 }).addTo(mapRef.current);
      markersRef.current = L.layerGroup().addTo(mapRef.current);
      const saleIcon = L.divIcon({ className: "commercial-marker", html: '<div style="width:12px;height:12px;border-radius:50%;background:#10b981;border:2px solid rgba(255,255,255,0.9);box-shadow:0 0 8px #10b981"></div>', iconSize: [12, 12] });
      const leaseIcon = L.divIcon({ className: "commercial-marker", html: '<div style="width:12px;height:12px;border-radius:50%;background:#3b82f6;border:2px solid rgba(255,255,255,0.9);box-shadow:0 0 8px #3b82f6"></div>', iconSize: [12, 12] });
      const bounds = L.latLngBounds();
      results.listings.forEach((l) => {
        const icon = l.type === "lease" ? leaseIcon : saleIcon;
        const marker = L.marker([l.lat, l.lng], { icon }).addTo(markersRef.current);
        marker.bindPopup(`<div style="font-size:12px;min-width:160px"><b>${l.type === "lease" ? "For Lease" : "For Sale"}</b><br/>ID: ${l.listing_id}<br/><a href="${l.url}" target="_blank" rel="noopener" style="color:#10b981;font-weight:600">View on LoopNet &rarr;</a></div>`);
        bounds.extend([l.lat, l.lng]);
      });
      mapRef.current.fitBounds(bounds, { padding: [30, 30] });
    };
    initMap();
  }, [results]);

  const enriched = results?.enriched_listings || [];

  return (
    <div data-testid="commercial-search-tab">
      <style>{`
        .leaflet-popup-content-wrapper { background: #1a1a2e; color: #e5e5e5; border: 1px solid #2d2d44; border-radius: 8px; }
        .leaflet-popup-tip { background: #1a1a2e; }
        .leaflet-popup-content a { color: #10b981; }
      `}</style>

      {/* Deal Calculator */}
      <DealCalculator />

      {/* Search */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Building2 className="w-4 h-4 text-blue-500" />
          <span className="text-xs font-medium text-blue-500">Search Listings via LoopNet</span>
        </div>
        <form onSubmit={handleSearch} className="flex gap-2 max-w-xl" data-testid="commercial-search-form">
          <div className="flex-1 relative">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Enter any US city (e.g., San Diego, CA)" className="pl-9 h-11 bg-card border-border" data-testid="commercial-search-input" />
          </div>
          <Button type="submit" className="h-11 px-6 bg-emerald-600 hover:bg-emerald-700" disabled={!searchQuery.trim() || loading} data-testid="commercial-search-btn">
            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <><Search className="w-4 h-4 mr-1" /> Search</>}
          </Button>
        </form>
      </div>

      {/* Quick Markets */}
      {!results && !loading && (
        <div className="mb-6" data-testid="commercial-quick-markets">
          <p className="text-xs text-muted-foreground uppercase font-semibold mb-3">Popular Markets</p>
          <div className="flex flex-wrap gap-2">
            {QUICK_MARKETS.map(m => (
              <button key={m} onClick={() => handleQuickMarket(m)} className="px-3 py-1.5 rounded-full text-xs font-medium bg-secondary/50 text-muted-foreground hover:text-foreground border border-border hover:border-emerald-500/30 transition-all" data-testid={`quick-market-${m.replace(/[, ]+/g, "-").toLowerCase()}`}>
                {m}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      {results && (
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 gap-3">
          <div className="flex items-center gap-3">
            <h2 className="text-base sm:text-lg font-bold" data-testid="commercial-results-title">{searchedLocation}</h2>
            <Badge variant="secondary" className="font-mono">{results.total || 0} listings</Badge>
          </div>
          <div className="flex gap-2" data-testid="commercial-filters">
            {["all", "sale", "lease"].map(t => (
              <button key={t} onClick={() => setListingType(t)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${listingType === t ? "bg-emerald-600 text-white" : "bg-secondary/50 text-muted-foreground border border-border"}`} data-testid={`commercial-filter-${t}`}>
                {t === "all" ? `All (${(results.sale_count || 0) + (results.lease_count || 0)})` : t === "sale" ? `Sale (${results.sale_count || 0})` : `Lease (${results.lease_count || 0})`}
              </button>
            ))}
          </div>
        </div>
      )}

      {loading && <div className="flex items-center justify-center h-64"><RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" /></div>}

      {/* Enriched Listings with Investment Analysis */}
      {enriched.length > 0 && !loading && (
        <div className="mb-6 space-y-3">
          <p className="text-xs text-muted-foreground uppercase font-semibold">Featured with Investment Analysis</p>
          {enriched.map(listing => (
            <Card key={listing.listing_id} className="bg-card border-border overflow-hidden" data-testid={`enriched-${listing.listing_id}`}>
              <CardContent className="p-0">
                <div className="flex flex-col sm:flex-row">
                  {listing.photo && (
                    <div className="w-full sm:w-44 h-36 sm:h-auto flex-shrink-0">
                      <img src={listing.photo.replace("{s}", "w")} alt={listing.title} className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="flex-1 p-3 sm:p-4 space-y-2">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div>
                        <h3 className="text-sm font-semibold">{listing.title || listing.property_name}</h3>
                        <p className="text-[10px] text-muted-foreground">{listing.city_line || listing.city_state}</p>
                      </div>
                      <p className="text-base font-mono font-bold text-emerald-400">{listing.price_display || "Contact"}</p>
                    </div>

                    {listing.analysis && (
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
                        <MetricBox label="Cap Rate" value={`${listing.analysis.cap_rate}%`} color={listing.analysis.cap_rate >= 7 ? "text-emerald-400" : "text-amber-400"} />
                        <MetricBox label="Est. NOI" value={formatCurrency(listing.analysis.noi)} color="text-emerald-400" />
                        <MetricBox label="Cash-on-Cash" value={`${listing.analysis.cash_on_cash_return}%`} color={listing.analysis.cash_on_cash_return >= 8 ? "text-emerald-400" : "text-amber-400"} />
                        <MetricBox label="DSCR" value={listing.analysis.dscr} color={listing.analysis.dscr >= 1.25 ? "text-emerald-400" : "text-amber-400"} />
                      </div>
                    )}

                    {listing.highlights?.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {listing.highlights.slice(0, 3).map((h, i) => (
                          <span key={i} className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400">{h}</span>
                        ))}
                      </div>
                    )}

                    {listing.summary && <p className="text-[10px] text-muted-foreground line-clamp-2">{listing.summary}</p>}

                    <a href={listing.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[10px] text-emerald-400 hover:text-emerald-300 font-medium">
                      View on LoopNet <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Map */}
      {results?.listings?.length > 0 && !loading && (
        <div className="mb-6">
          <div ref={mapContainerRef} className="w-full h-[350px] sm:h-[450px] rounded-xl border border-border overflow-hidden" data-testid="commercial-map" />
          <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500" /> For Sale</span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-blue-500" /> For Lease</span>
            <span className="ml-auto">Click markers for details</span>
          </div>
        </div>
      )}

      {/* Basic Listings Grid */}
      {results?.listings?.length > 0 && !loading && (
        <div>
          <p className="text-xs text-muted-foreground mb-3">
            {results.listings.length > 50 ? `Showing 50 of ${results.listings.length}` : `${results.listings.length} listings`} — Click to view on LoopNet
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 sm:gap-3" data-testid="commercial-listings-grid">
            {results.listings.slice(0, 50).map(l => (
              <a key={l.listing_id} href={l.url} target="_blank" rel="noopener noreferrer" className="block" data-testid={`commercial-listing-${l.listing_id}`}>
                <Card className="bg-card border-border card-hover h-full">
                  <CardContent className="p-2.5 sm:p-3">
                    <div className="flex items-center justify-between mb-1.5">
                      <Badge className={`text-[8px] sm:text-[9px] border-0 ${l.type === "sale" ? "bg-emerald-500/15 text-emerald-500" : "bg-blue-500/15 text-blue-500"}`}>
                        {l.type === "sale" ? "For Sale" : "For Lease"}
                      </Badge>
                      <ExternalLink className="w-3 h-3 text-muted-foreground" />
                    </div>
                    <p className="text-[10px] font-mono text-muted-foreground mb-0.5">ID: {l.listing_id}</p>
                    <p className="text-[9px] text-muted-foreground/60">{l.lat.toFixed(4)}, {l.lng.toFixed(4)}</p>
                    <p className="text-[9px] text-emerald-500 font-medium mt-1">View on LoopNet</p>
                  </CardContent>
                </Card>
              </a>
            ))}
          </div>
        </div>
      )}

      {/* States */}
      {results?.message && !loading && (
        <div className="text-center py-16"><Building2 className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" /><p className="text-muted-foreground">{results.message}</p></div>
      )}
      {results && results.total === 0 && !results.message && !loading && (
        <div className="text-center py-16"><Building2 className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" /><p className="text-muted-foreground">No commercial listings found in this area.</p></div>
      )}
      {!results && !loading && (
        <div className="text-center py-8">
          <Building2 className="w-12 h-12 text-muted-foreground/20 mx-auto mb-3" />
          <h3 className="text-base font-bold mb-1">Search any US city</h3>
          <p className="text-xs text-muted-foreground max-w-md mx-auto">Discover commercial properties for sale and lease on an interactive map.</p>
        </div>
      )}
    </div>
  );
};

export default CommercialSearchTab;
